import { trackEvent, axiosInstance, logger } from "@hopper-b2b/api";
import {
  type IApiConfig,
  NETWORK_CALL_FAILED,
  TripCategory,
} from "@hopper-b2b/types";
import { isSafeCommerceReturnPath } from "../../../../utils/nupayCommerceCheckoutPath";
import {
  isLegacyCheckoutPath,
  isSafeLegacyReturnPath,
  sanitizeLegacyReturnSearch,
} from "../../../../utils/nupayLegacyReturnContext";
import { checkAuthStatusPath } from "./paths";

export const PAYMENT_ENROLLMENT_STATUS_PARAM = "enrollment";

// Nupay direct OAuth callback query params
export const NUPAY_AUTH_CODE_PARAM = "code";
export const NUPAY_ERROR_PARAM = "error";
export const NUPAY_ERROR_DESCRIPTION_PARAM = "error_description";
export const NUPAY_STATE_PARAM = "state";

// localStorage key for legacy checkout context when using minimal OAuth state.
// Commerce-portals writes the same key to sessionStorage (platform: "commerce").
export const NUPAY_CHECKOUT_CONTEXT_KEY = "nupay_checkout_context";

const LEGACY_COMMERCE_CONTEXT_KEY = "nupay_commerce_return_context";

// Timeout for the check auth status API call (30 seconds)
const CHECK_AUTH_TIMEOUT_MS = 30000;

export enum EnrollmentResponse {
  success = "success",
  failure = "failure",
}

/**
 * NupayState carries all checkout context through the NuPay OAuth redirect.
 *
 * Because NuPay only allows a single redirect_uri with no path components
 * (e.g., https://nuviagens.com/), the state parameter is used to carry
 * the checkout context so the frontend can restore the session after redirect.
 *
 * The BFF adds a CSRF token to the state before forwarding to NuPay.
 * On redirect back, the frontend deserializes state to recover context.
 *
 * Mirrors the BFF's NupayState case class.
 */
export interface NupayState {
  // csrfToken is set by the BFF — not sent from FE, present in callback state
  csrfToken?: string;

  // Trip Details
  tripCategory?: string;
  origin?: string;
  originType?: string;
  destination?: string;
  destinationType?: string;
  departureDate?: string;
  returnDate?: string;
  stopsOption?: string;
  tripId?: string;

  // Passenger Details
  adultsCount?: number;
  childrenCount?: number;
  infantsInSeatCount?: number;
  infantsOnLapCount?: number;
  selectedPassengerIds?: string;
  selectedLapInfantIds?: string;

  // User/Contact Info (encrypted)
  email?: string;
  phone?: string;

  // Cart & Fare Status
  checkoutState?: string;
  cartToken?: string;
  outgoingFareId?: string;
  returnFareId?: string;
  flightShopProgress?: number;
  flightShopType?: string;
  noLCC?: boolean;
  priceDropCandidateId?: string;

  // Lodging checkout (hotel book resume)
  availabilityLink?: string;
  selectedWalletOffer?: string;
  fromDate?: string;
  untilDate?: string;
  placeId?: string;
  query?: string;
  lodgingCity?: string;
}

/**
 * Build a NupayState from the current URL query params.
 * This captures all checkout context needed to resume after OAuth redirect.
 */
export const buildNupayState = (): NupayState => {
  const searchParams = new URLSearchParams(window.location.search);

  const optStr = (key: string): string | undefined =>
    searchParams.get(key) ?? undefined;
  const optInt = (key: string): number | undefined => {
    const val = searchParams.get(key);
    if (val == null) return undefined;
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? undefined : parsed;
  };
  const optBool = (key: string): boolean | undefined => {
    const val = searchParams.get(key);
    if (val == null) return undefined;
    return val === "true";
  };

  const returnDate = optStr("returnDate");
  const rawTripCategory = optStr("tripCategory");

  // Infer tripCategory from returnDate when the URL param is missing or
  // unrecognized. The hydration-race window (PR #18256) can transiently drop
  // tripCategory from the URL before buildNupayState() runs, producing a stored
  // NupayState with no tripCategory. After the OAuth callback the restored URL
  // then lacks tripCategory too, so the RestartCheckout step-1 validator fails
  // and the user is immediately bounced to /flights/search (the < 200ms bounce
  // the monitor catches). Falling back to ONE_WAY when returnDate is also absent
  // is safe because Price Watch deep links for one-way trips never carry
  // returnDate.
  //
  // An explicit URL value always wins, but is normalized to the TripCategory
  // enum (rather than passed through as a raw string) so the stored value has a
  // single, consistent type regardless of whether it came from the URL or from
  // inference.
  const tripCategory: TripCategory =
    rawTripCategory === TripCategory.ONE_WAY ||
    rawTripCategory === TripCategory.ROUND_TRIP
      ? (rawTripCategory as TripCategory)
      : returnDate
        ? TripCategory.ROUND_TRIP
        : TripCategory.ONE_WAY;

  return {
    tripCategory,
    origin: optStr("origin"),
    originType: optStr("originType"),
    destination: optStr("destination"),
    destinationType: optStr("destinationType"),
    departureDate: optStr("departureDate"),
    returnDate,
    stopsOption: optStr("stopsOption"),
    tripId: optStr("tripId"),
    adultsCount: optInt("adultsCount"),
    childrenCount: optInt("childrenCount"),
    infantsInSeatCount: optInt("infantsInSeatCount"),
    infantsOnLapCount: optInt("infantsOnLapCount"),
    selectedPassengerIds: optStr("selectedPassengerIds"),
    selectedLapInfantIds: optStr("selectedLapInfantIds"),
    email: optStr("email"),
    phone: optStr("phone"),
    checkoutState: optStr("checkoutState"),
    cartToken: optStr("cartToken"),
    outgoingFareId: optStr("outgoingFareId"),
    returnFareId: optStr("returnFareId"),
    flightShopProgress: optInt("flightShopProgress"),
    flightShopType: optStr("flightShopType"),
    noLCC: optBool("noLCC"),
    priceDropCandidateId: optStr("priceDropCandidateId"),
    availabilityLink: optStr("availabilityLink"),
    selectedWalletOffer: optStr("selectedWalletOffer"),
    fromDate: optStr("fromDate"),
    untilDate: optStr("untilDate"),
    placeId: optStr("placeId"),
    query: optStr("query"),
    lodgingCity: optStr("lodgingCity"),
  };
};

/**
 * Parse the NupayState from a base64-encoded state query param.
 * Tries base64 JSON first, then raw JSON as fallback.
 * Returns undefined if the state param is missing or not valid JSON
 * (e.g. a UUID from the minimal state flow).
 */
export const parseNupayState = (
  stateParam: string | null | undefined
): NupayState | undefined => {
  if (!stateParam) return undefined;

  try {
    const binaryStr = atob(stateParam);
    const bytes = Uint8Array.from(binaryStr, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as NupayState;
  } catch {
    try {
      return JSON.parse(stateParam) as NupayState;
    } catch {
      return undefined;
    }
  }
};

/**
 * Encode a NupayState as a base64 string.
 * Used in tests and for decoding state returned by NuPay callback.
 * Note: In production, the BFF handles base64 encoding before sending to NuPay.
 */
export const encodeNupayState = (state: NupayState): string => {
  // Use unescape(encodeURIComponent(...)) to safely handle any Unicode
  // characters in state fields (e.g. accented city names) since btoa()
  // only supports Latin-1. The BFF decodes this as plain base64(JSON).
  return btoa(unescape(encodeURIComponent(JSON.stringify(state))));
};

/**
 * Reconstruct a URL search string from a NupayState.
 * Used after OAuth redirect to restore checkout URL params.
 */
export const nupayStateToSearchParams = (state: NupayState): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(state)) {
    // Skip csrfToken — it's a BFF-internal field, not a checkout URL param
    if (key === "csrfToken") continue;
    if (value != null) {
      params.set(key, String(value));
    }
  }
  return params.toString();
};

/**
 * Save checkout context to localStorage before OAuth redirect.
 * The BFF sends a UUID-only state to NuPay, so the FE persists
 * checkout context in localStorage instead.
 */
// localStorage entries older than this are considered stale and discarded.
const NUPAY_CONTEXT_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Dedicated key for persisting the payment type (NupayPayment / EbanxNupayPayment)
// across enrollment OAuth redirects (both NuPay Direct and EBANX flows).
const NUPAY_PAYMENT_TYPE_KEY = "nupay_payment_type";

export type NupayPaymentType = "NupayPayment" | "EbanxNupayPayment";

const isValidPaymentType = (value: string): value is NupayPaymentType =>
  value === "NupayPayment" || value === "EbanxNupayPayment";

/**
 * Save the BFF-selected payment type to localStorage so it survives the page
 * reload that follows an enrollment OAuth redirect. Wraps the value with a
 * timestamp so stale entries are rejected on restore. Silently discards unknown
 * payment type values at write time.
 */
export const savePaymentTypeToStorage = (
  paymentType: string | undefined
): void => {
  if (!paymentType) return;
  if (!isValidPaymentType(paymentType)) {
    console.warn(
      "[NupayDirect] Unknown payment type, not persisting:",
      paymentType
    );
    return;
  }
  try {
    const wrapped = { paymentType, savedAt: Date.now() };
    localStorage.setItem(NUPAY_PAYMENT_TYPE_KEY, JSON.stringify(wrapped));
  } catch (e) {
    console.warn(
      "[NupayDirect] Failed to save payment type to localStorage:",
      e
    );
  }
};

/**
 * Restore the payment type from localStorage after an enrollment OAuth callback.
 * Returns the stored value and removes it (one-time use).
 * Returns null if the entry is missing, stale, or not a recognised payment type.
 */
export const consumePaymentTypeFromStorage = (): NupayPaymentType | null => {
  try {
    const json = localStorage.getItem(NUPAY_PAYMENT_TYPE_KEY);
    if (!json) return null;
    localStorage.removeItem(NUPAY_PAYMENT_TYPE_KEY);
    const wrapped = JSON.parse(json) as {
      paymentType?: string;
      savedAt?: number;
    };
    if (
      !wrapped.savedAt ||
      Date.now() - wrapped.savedAt > NUPAY_CONTEXT_TTL_MS
    ) {
      console.warn(
        "[NupayDirect] Stale payment type in localStorage, discarding"
      );
      return null;
    }
    if (!wrapped.paymentType || !isValidPaymentType(wrapped.paymentType)) {
      console.warn(
        "[NupayDirect] Invalid payment type in localStorage, discarding"
      );
      return null;
    }
    return wrapped.paymentType;
  } catch (e) {
    console.warn(
      "[NupayDirect] Failed to read payment type from localStorage:",
      e
    );
    return null;
  }
};

export type StoredCommerceCheckoutContext = {
  platform: "commerce";
  returnPath: string;
  returnSearch: string;
  quoteId: string;
  savedAt: number;
};

export type StoredLegacyCheckoutContext = {
  platform: "legacy";
  state: NupayState;
  /** Legacy book checkout pathname (e.g. /hotels/book/) when OAuth started. */
  returnPath?: string;
  /** Sanitized query string without OAuth callback params. */
  returnSearch?: string;
  savedAt: number;
};

export type StoredNupayCheckoutContext =
  | StoredCommerceCheckoutContext
  | StoredLegacyCheckoutContext;

const SAFE_QUOTE_ID_PATTERN = /^[A-Za-z0-9._-]{1,2048}$/;

function readStoredCheckoutContextJson(): string | null {
  return (
    sessionStorage.getItem(NUPAY_CHECKOUT_CONTEXT_KEY) ??
    localStorage.getItem(NUPAY_CHECKOUT_CONTEXT_KEY) ??
    localStorage.getItem(LEGACY_COMMERCE_CONTEXT_KEY)
  );
}

function removeStoredCheckoutContextKeys(): void {
  sessionStorage.removeItem(NUPAY_CHECKOUT_CONTEXT_KEY);
  localStorage.removeItem(NUPAY_CHECKOUT_CONTEXT_KEY);
  localStorage.removeItem(LEGACY_COMMERCE_CONTEXT_KEY);
}

function parseStoredCommerceCheckoutContext(
  parsed: Record<string, unknown>
): StoredCommerceCheckoutContext | null {
  const returnPath = parsed.returnPath;
  const returnSearch = parsed.returnSearch;
  const quoteId = parsed.quoteId;
  const savedAt = parsed.savedAt;

  if (
    typeof returnPath !== "string" ||
    typeof returnSearch !== "string" ||
    typeof quoteId !== "string" ||
    typeof savedAt !== "number"
  ) {
    return null;
  }

  if (
    Date.now() - savedAt > NUPAY_CONTEXT_TTL_MS ||
    !isSafeCommerceReturnPath(returnPath) ||
    !SAFE_QUOTE_ID_PATTERN.test(quoteId)
  ) {
    return null;
  }

  return {
    platform: "commerce",
    returnPath,
    returnSearch,
    quoteId,
    savedAt,
  };
}

function parseStoredCheckoutContextJson(
  json: string
): StoredNupayCheckoutContext | null {
  try {
    const parsed = JSON.parse(json) as Record<string, unknown>;

    if (
      parsed.platform === "commerce" ||
      (typeof parsed.returnPath === "string" &&
        typeof parsed.quoteId === "string" &&
        parsed.state == null)
    ) {
      return parseStoredCommerceCheckoutContext(parsed);
    }

    const state = parsed.state;
    const savedAt =
      typeof parsed.savedAt === "number" ? parsed.savedAt : Date.now();

    if (!state || typeof state !== "object") {
      console.warn("[NupayDirect] Invalid checkout context in localStorage");
      return null;
    }

    if (Date.now() - savedAt > NUPAY_CONTEXT_TTL_MS) {
      console.warn(
        "[NupayDirect] Stale checkout context in localStorage, discarding"
      );
      return null;
    }

    const returnPath = parsed.returnPath;
    const returnSearch = parsed.returnSearch;
    const hasReturnPath = typeof returnPath === "string";
    const hasReturnSearch = typeof returnSearch === "string";

    let validatedReturnPath: string | undefined;
    let validatedReturnSearch: string | undefined;

    if (hasReturnPath) {
      if (!isSafeLegacyReturnPath(returnPath)) {
        console.warn(
          "[NupayDirect] Invalid legacy checkout return path in localStorage, ignoring"
        );
      } else {
        validatedReturnPath = returnPath;
        validatedReturnSearch = hasReturnSearch
          ? sanitizeLegacyReturnSearch(returnSearch)
          : "";
      }
    } else if (hasReturnSearch) {
      validatedReturnSearch = sanitizeLegacyReturnSearch(returnSearch);
    }

    return {
      platform: "legacy",
      state: state as NupayState,
      savedAt,
      ...(validatedReturnPath && {
        returnPath: validatedReturnPath,
      }),
      ...(validatedReturnSearch !== undefined && {
        returnSearch: validatedReturnSearch,
      }),
    };
  } catch (e) {
    console.warn(
      "[NupayDirect] Failed to restore checkout context from localStorage:",
      e
    );
    return null;
  }
}

/** Read checkout context without removing it (for misroute detection). */
export function peekStoredCheckoutContextFromStorage(): StoredNupayCheckoutContext | null {
  const json = readStoredCheckoutContextJson();
  if (!json) return null;
  return parseStoredCheckoutContextJson(json);
}

export function consumeStoredCheckoutContextFromStorage(): StoredNupayCheckoutContext | null {
  const stored = peekStoredCheckoutContextFromStorage();
  if (stored) {
    removeStoredCheckoutContextKeys();
  }
  return stored;
}

export const saveCheckoutContextToStorage = (state: NupayState): boolean => {
  try {
    const pathname = window.location.pathname;
    const onLegacyCheckout = isLegacyCheckoutPath(pathname);

    const wrapped: StoredLegacyCheckoutContext = {
      platform: "legacy",
      state,
      savedAt: Date.now(),
      ...(onLegacyCheckout && {
        returnPath: pathname,
        returnSearch: sanitizeLegacyReturnSearch(window.location.search),
      }),
    };
    localStorage.setItem(NUPAY_CHECKOUT_CONTEXT_KEY, JSON.stringify(wrapped));
    return true;
  } catch (e) {
    console.warn(
      "[NupayDirect] Failed to save checkout context to localStorage:",
      e
    );
    return false;
  }
};

/**
 * Restore checkout context from localStorage after OAuth callback.
 * Returns the saved NupayState and removes it from storage (one-time use).
 * Safe to call multiple times — returns null after the first successful consume.
 */
export const consumeCheckoutContextFromStorage = (): NupayState | null => {
  const stored = consumeStoredCheckoutContextFromStorage();
  if (!stored || stored.platform !== "legacy") {
    return null;
  }
  return stored.state;
};

/**
 * Check if a parsed NupayState is a minimal/UUID-only state (no checkout fields).
 */
export const isMinimalNupayState = (state: NupayState): boolean =>
  !state.origin && !state.tripCategory && !state.cartToken;

interface CheckAuthStatusOptions {
  apiConfig?: IApiConfig;
  nupayDirect?: boolean;
}

export const checkAuthStatus = (
  options?: CheckAuthStatusOptions
): Promise<GetPreAuthorizeStatusResponse> =>
  new Promise((resolve, reject) => {
    const locationUrl = new URL(window.location.toString());
    const searchParams = new URLSearchParams(locationUrl.search);

    // Clean up any existing enrollment/nupay params
    searchParams.delete(PAYMENT_ENROLLMENT_STATUS_PARAM);
    searchParams.delete(NUPAY_AUTH_CODE_PARAM);
    searchParams.delete(NUPAY_ERROR_PARAM);
    searchParams.delete(NUPAY_ERROR_DESCRIPTION_PARAM);
    searchParams.delete(NUPAY_STATE_PARAM);

    const filteredLocationString = `${locationUrl.origin}${
      locationUrl.pathname
    }?${searchParams.toString()}`;

    const nupayState = options?.nupayDirect ? buildNupayState() : undefined;

    // Save checkout context to localStorage before the NuPay OAuth redirect.
    // The BFF sends a UUID-only state to NuPay; the FE restores context from
    // localStorage on callback.
    if (nupayState && !saveCheckoutContextToStorage(nupayState)) {
      logger()?.warn("Failed to save NuPay checkout context to localStorage");
    }

    const payload: GetPreAuthorizeStatusRequest = options?.nupayDirect
      ? {
          redirectUrl: locationUrl.origin,
          successRedirectUrl: `${filteredLocationString}&${PAYMENT_ENROLLMENT_STATUS_PARAM}=${EnrollmentResponse.success}`,
          failureRedirectUrl: `${filteredLocationString}&${PAYMENT_ENROLLMENT_STATUS_PARAM}=${EnrollmentResponse.failure}`,
          state: nupayState,
        }
      : {
          successRedirectUrl: `${filteredLocationString}&${PAYMENT_ENROLLMENT_STATUS_PARAM}=${EnrollmentResponse.success}`,
          failureRedirectUrl: `${filteredLocationString}&${PAYMENT_ENROLLMENT_STATUS_PARAM}=${EnrollmentResponse.failure}`,
        };

    axiosInstance
      .post(checkAuthStatusPath, payload, {
        timeout: CHECK_AUTH_TIMEOUT_MS,
      })
      .then((res) => {
        const responseBody = res.data;
        savePaymentTypeToStorage(responseBody.value?.payment);
        resolve(responseBody);
      })
      .catch((e) => {
        const isTimeout =
          e?.code === "ECONNABORTED" || e?.message?.includes("timeout");
        trackEvent(
          {
            eventName: NETWORK_CALL_FAILED,
            properties: {
              url: window.location.pathname,
              error_type: isTimeout ? "timeout" : "network_error",
              error_message: e?.message,
            },
          },
          options?.apiConfig
        );
        reject(e);
      });
  });

interface GetPreAuthorizeStatusRequest {
  successRedirectUrl?: string;
  failureRedirectUrl?: string;
  redirectUrl?: string;
  state?: NupayState;
}

export enum PreAuthorizeCode {
  APPROVAL_NEEDED = "APPROVAL_NEEDED",
  ALREADY_APPROVED = "ALREADY_APPROVED",
}

export interface GetPreAuthorizeStatusResponse {
  Response: "Success" | "Failure";
  value: {
    /**
     * "APPROVAL_NEEDED" or "ALREADY_APPROVED".
     */
    code: PreAuthorizeCode;
    /**
     * A URL to redirect the user to in order to get their
     * pre-authorization.
     */
    redirectUrl?: string;
    /**
     * The payment type selected by the BFF for this pre-authorization.
     * Either "NupayPayment" (HopperBR / NuPay Direct) or "EbanxNupayPayment" (HopperInc / EBANX).
     * Used by the frontend to send the correct payment type back in the fulfill request.
     */
    payment?: string;
  };
}
